import { useState, useEffect } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { TraderSetSidebar } from '../../components/TraderSetSidebar'
import { useExecutions } from '../../context/ExecutionContext'
import { useAuth } from '../../context/AuthContext'
import { getLotSize } from '../../utils/lotSize'
import { useToast } from '../../context/ToastContext'
import { useMarketData, NAME_TO_TOKEN } from '../../context/MarketDataContext'
import { calcGreeks, calculateTimeToExpiry } from '../../utils/greeks'
import type { StrategyExecution } from '../../types'

/* ─── Strategy metadata ─── */

const strategyMeta: Record<string, { label: string; icon: string; color: string; desc: string }> = {
  rsi_straddle: {
    label: 'RSI Straddle',
    icon: '⟐',
    color: '#38bdf8',
    desc: 'Short ATM straddle at open. Trail SL on 10-min candle. VIX RSI governs re-entry (max 3).',
  },
  trending_expiry: {
    label: 'Trending Expiry',
    icon: '⏱',
    color: '#a78bfa',
    desc: 'Sell straddles every 30 min on expiry days. Asymmetric SL per leg. Extra OTM hedge on SL hit.',
  },
}

/* ─── Component ─── */

export function TraderStrategies() {
  const {
    executions, startExecution, stopExecution, panicExit, partialExit, createExecution, deleteExecution, refreshExecutions,
  } = useExecutions()
  const { addToast } = useToast()
  const { session } = useAuth()
  const traderId = session?.user?.id || 'trader1'
  const { ticks } = useMarketData()

  // Load executions from API on mount
  useEffect(() => { refreshExecutions() }, [refreshExecutions])

  const [sidebarOpen, setSidebarOpen] = useState<string | null>(null)
  const [panicModal, setPanicModal] = useState<{ level: 'strategy' | 'set'; target: string; label: string } | null>(null)
  const [partialModal, setPartialModal] = useState<string | null>(null)
  const [partialCe, setPartialCe] = useState(0)
  const [partialPe, setPartialPe] = useState(0)
  const [legsExpanded, setLegsExpanded] = useState<Record<string, boolean>>({})

  const handleStartAll = (stratType: string) => {
    const stoppedSets = executions.filter(e => e.strategyType === stratType && e.status === 'stopped')
    stoppedSets.forEach(e => startExecution(e.id))
    addToast(`Started ${stoppedSets.length} ${strategyMeta[stratType]?.label} set(s)`, 'success')
  }

  const handleStopAll = (stratType: string) => {
    const runningSets = executions.filter(e => e.strategyType === stratType && e.status === 'running')
    runningSets.forEach(e => stopExecution(e.id))
    addToast(`Stopped ${runningSets.length} ${strategyMeta[stratType]?.label} set(s)`, 'warning')
  }

  const handlePanicConfirm = () => {
    if (!panicModal) return
    if (panicModal.level === 'strategy') {
      const sets = executions.filter(e => e.strategyType === panicModal.target && e.status === 'running')
      sets.forEach(e => panicExit('set', e.id))
      addToast(`Panic exit — ${panicModal.label} positions squared off`, 'warning', 6000)
    } else {
      panicExit('set', panicModal.target)
      addToast(`Panic exit — ${panicModal.label} squared off`, 'warning', 6000)
    }
    setPanicModal(null)
  }

  const handlePartialExit = () => {
    if (!partialModal) return
    partialExit(partialModal, partialCe, partialPe)
    const exec = executions.find((e) => e.id === partialModal)
    addToast(`Partial exit on ${exec?.setName || partialModal} — CE: ${partialCe}%, PE: ${partialPe}%`, 'success')
    setPartialModal(null)
    setPartialCe(0)
    setPartialPe(0)
  }

  const handleDelete = (exec: StrategyExecution) => {
    if (exec.status === 'running') {
      addToast('Stop the set first before deleting', 'error')
      return
    }
    deleteExecution(exec.id)
    addToast(`${exec.setName} deleted`, 'info')
  }

  const handleStart = (id: string) => {
    startExecution(id)
    const exec = executions.find((e) => e.id === id)
    addToast(`${exec?.setName || 'Execution'} started`, 'success')
  }

  const handleStop = (id: string) => {
    stopExecution(id)
    const exec = executions.find((e) => e.id === id)
    addToast(`${exec?.setName || 'Execution'} stopped`, 'warning')
  }

  /* ─── Render exec card (same as Dashboard) ─── */
  const renderExecCard = (exec: StrategyExecution) => {
    const isExpanded = legsExpanded[exec.id]
    const margin = exec.margin || ((exec.params as any)?.marginPerLot || 150000)
    const limit = margin * 0.02
    const loss = exec.state.pnl < 0 ? Math.abs(exec.state.pnl) : 0
    const ddPercent = Math.min(100, Math.round((loss / limit) * 100))
    let ddColor = '#22c55e'
    if (ddPercent >= 80) ddColor = '#ef4444'
    else if (ddPercent >= 40) ddColor = '#eab308'
    const openLegs = (exec.state.straddleEntries?.reduce((a, e) => a + (e.call.status === 'open' ? 1 : 0) + (e.put.status === 'open' ? 1 : 0), 0) || 0) + (exec.state.extraLegs?.filter(l => l.status === 'open').length || 0)
    const isRunning = exec.status === 'running'
    const isPaused = exec.status === 'paused'
    const isStopped = exec.status === 'stopped'
    const isSquaredOff = exec.status === 'squared_off'
    const statusColor = isRunning ? 'var(--green)' : isPaused ? 'var(--yellow)' : isSquaredOff ? 'var(--orange, #f97316)' : 'var(--red)'
    const statusLabel = isRunning ? 'RUNNING' : isPaused ? 'PAUSED' : isSquaredOff ? 'SQUARED OFF' : 'STOPPED'
    const p = exec.params as any
    const lots = p?.quantityLots || 1
    const lotSize = getLotSize(exec.underlying)
    const qty = lots * lotSize

    // -- Compute Greeks --
    let greeks = { delta: '—', gamma: '—', theta: '—', vega: '—' }
    if (isRunning) {
      const currentEntry = exec.state.straddleEntries?.[exec.state.straddleEntries.length - 1]
      const strike = currentEntry?.strike || (exec.state.currentStraddle as any)?.call?.strike || (exec.state.currentStraddle as any)?.strike || 0
      const expiry = (currentEntry as any)?.call?.expiry || (exec.state.currentStraddle as any)?.call?.expiry || (exec.state.currentStraddle as any)?.expiry || ''
      const token = NAME_TO_TOKEN[exec.underlying]
      const tick = token ? ticks[token] : null
      const S = tick?.ltp || 0
      const T = calculateTimeToExpiry(expiry)

      if (strike > 0 && S > 0) {
        // Average the premium into one leg for approx Greeks
        const legPrice = exec.state.currentPremium / 2
        // Default to CE calculation for the straddle, approx IV
        const iv = 0.2 // Simplified IV for real-time without blocking UI thread
        const legGreeks = calcGreeks('CE', S, strike, T, 0.07, iv)
        // For a straddle (CE + PE short), Delta = -(CE Delta + PE Delta), Gamma = -(CE Gamma + PE Gamma)
        // CE Delta = Nd1, PE Delta = Nd1 - 1
        // Straddle Delta = Nd1 + Nd1 - 1 = 2*Nd1 - 1
        // Short Straddle Delta = 1 - 2*Nd1
        const deltaStraddle = -(legGreeks.delta + (legGreeks.delta - 1))
        const gammaStraddle = -(legGreeks.gamma * 2)
        const thetaStraddle = -(legGreeks.theta * 2) // Short collects theta -> positive
        const vegaStraddle = -(legGreeks.vega * 2) // Short loses on vol -> negative

        greeks = {
          delta: deltaStraddle.toFixed(3),
          gamma: gammaStraddle.toFixed(4),
          theta: thetaStraddle.toFixed(1),
          vega: vegaStraddle.toFixed(1),
        }
      }
    }

    return (
      <div className="xc" key={exec.id} style={{ '--xc-accent': statusColor } as React.CSSProperties}>
        {/* ── Row 1: Identity + Chips + Status + Actions ── */}
        <div className="xc-row1">
          <div className="xc-id">
            <span className="xc-dot" style={{ background: statusColor }} />
            <span className="xc-name">{exec.setName}</span>
            <span className="xc-type">{exec.strategyType === 'rsi_straddle' ? 'RSI Straddle' : 'Trending'}</span>
          </div>
          <div className="xc-chips">
            <span className="xc-chip xc-chip--hl">{exec.underlying}</span>
            <span className="xc-chip">{p?.expiryType || 'weekly'}</span>
            <span className="xc-chip">{p?.strikeSelectionMode || 'ATM'}{p?.strikeOffset ? (p.strikeOffset > 0 ? `+${p.strikeOffset}` : p.strikeOffset) : ''}</span>
            <span className="xc-chip">{p?.positionType === 'strangle' ? 'Strangle' : 'Straddle'}</span>
            <span className="xc-chip">{p?.executionMode === 'instant' ? '⚡ Instant' : '📅 Scheduled'}</span>
            <span className="xc-chip">{exec.mode === 'single_client' ? 'Single' : 'Multi'}</span>
            <span className="xc-sep" />
            <span className="xc-chip xc-chip--mono">Lot: {lots}</span>
            <span className="xc-chip xc-chip--mono">Qty: {qty}</span>
          </div>
          <div className="xc-right">
            {exec.state.lastPartialExit && (
              <span className="xc-partial-flag" title={`Partial exit — CE: ${exec.state.lastPartialExit.cePercent}%, PE: ${exec.state.lastPartialExit.pePercent}%`}>
                ◐ Partial ({exec.state.lastPartialExit.cePercent}/{exec.state.lastPartialExit.pePercent}%)
              </span>
            )}
            <span className={`xc-status xc-status--${exec.status}`}>{statusLabel}</span>
            <div className="xc-btns">
              {(isStopped || isSquaredOff) && <button className="btn btn-sm btn-green" onClick={() => handleStart(exec.id)}>▶ Start</button>}
              {isRunning && <button className="btn btn-sm btn-red" onClick={() => handleStop(exec.id)}>■ Stop</button>}
              {isPaused && <button className="btn btn-sm btn-green" onClick={() => handleStart(exec.id)}>▶ Resume</button>}
              {!isSquaredOff && <button className="btn btn-sm btn-ghost" onClick={() => { setPartialCe(0); setPartialPe(0); setPartialModal(exec.id) }}>↔ Partial</button>}
              {!isSquaredOff && <button className="btn btn-sm btn-red" onClick={() => setPanicModal({ level: 'set', target: exec.id, label: exec.setName })}>⚠ Exit</button>}
              {isStopped && <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)', fontSize: '0.6875rem' }} onClick={() => handleDelete(exec)}>✕</button>}
            </div>
          </div>
        </div>

        {/* ── Row 2: Metrics + Greeks + VIX + Legs ── */}
        <div className="xc-row2">
          <div className="xc-metrics">
            <span className="xc-m"><span className="xc-ml">Premium</span><span className="xc-mv xc-mv--accent">₹{(exec.state.currentPremium || 0).toLocaleString()}</span></span>
            <span className="xc-m"><span className="xc-ml">P&L</span><span className={`xc-mv ${(exec.state.pnl || 0) >= 0 ? 'xc-mv--green' : 'xc-mv--red'}`}>{(exec.state.pnl || 0) >= 0 ? '+' : ''}₹{(exec.state.pnl || 0).toLocaleString()}</span></span>
            <span className="xc-m"><span className="xc-ml">MTM</span><span className={`xc-mv ${(exec.state.mtm?.totalPnl || 0) >= 0 ? 'xc-mv--green' : 'xc-mv--red'}`}>{(exec.state.mtm?.totalPnl || 0) >= 0 ? '+' : ''}₹{(exec.state.mtm?.totalPnl || 0).toLocaleString()}</span></span>
            <span className="xc-m xc-m--margin">
              <span className="xc-ml">Margin</span>
              <span className="xc-margin-wrap">
                <span className="xc-margin-track"><span className="xc-margin-fill" style={{ width: `${ddPercent}%`, background: ddColor }} /></span>
                <span className="xc-mv" style={{ color: ddColor }}>{ddPercent}%</span>
              </span>
            </span>
          </div>
          <span className="xc-sep" />
          <div className="xc-greeks">
            <span className="xc-g"><span className="xc-gl">Delta</span><span className="xc-gv">{greeks.delta}</span></span>
            <span className="xc-g"><span className="xc-gl">Gamma</span><span className="xc-gv">{greeks.gamma}</span></span>
            <span className="xc-g"><span className="xc-gl">Theta</span><span className="xc-gv">{greeks.theta}</span></span>
            <span className="xc-g"><span className="xc-gl">Vega</span><span className="xc-gv">{greeks.vega}</span></span>
          </div>
          <span className="xc-sep" />
          {exec.state.vixRsi !== undefined ? (
            <span className="xc-vix" style={{ color: exec.state.isCooldown ? 'var(--red)' : 'var(--green)' }}>VIX {exec.state.vixRsi.toFixed(0)} {exec.state.isCooldown ? '⏳' : '✓'}</span>
          ) : <span className="xc-vix" style={{ color: 'var(--text-muted)' }}>VIX —</span>}
          <span className="xc-re">Re-entry: {exec.state.reentryCount || 0}</span>
          <button className="xc-legs-btn" onClick={() => setLegsExpanded(prev => ({ ...prev, [exec.id]: !prev[exec.id] }))}>
            {isExpanded ? '▲' : '▼'} {openLegs} Legs
          </button>
        </div>

        {/* ── Expandable Legs ── */}
        {isExpanded && exec.state.straddleEntries && exec.state.straddleEntries.length > 0 && (
          <div className="xc-legs">
            {exec.state.straddleEntries.map((entry) => (
              <div key={entry.id} className="xc-leg-row">
                <span className="xc-leg"><span className="xc-leg-dot" style={{ background: entry.call.status === 'open' ? 'var(--green)' : 'var(--text-muted)' }} /><strong>{entry.strike}CE</strong><span className="xc-leg-d">@₹{entry.call.entryPrice} SL ₹{entry.call.slPrice} Tgt ₹{entry.call.targetPrice}</span></span>
                <span className="xc-leg"><span className="xc-leg-dot" style={{ background: entry.put.status === 'open' ? 'var(--green)' : 'var(--text-muted)' }} /><strong>{entry.strike}PE</strong><span className="xc-leg-d">@₹{entry.put.entryPrice} SL ₹{entry.put.slPrice} Tgt ₹{entry.put.targetPrice}</span></span>
              </div>
            ))}
            {exec.state.extraLegs && exec.state.extraLegs.map((leg) => (
              <span key={leg.id} className="xc-leg"><span className="xc-leg-dot" style={{ background: leg.status === 'open' ? 'var(--accent)' : 'var(--text-muted)' }} /><strong>{leg.strike}{leg.optionType}</strong><span className="xc-leg-d">@₹{leg.entryPrice} SL ₹{leg.slPrice}</span><em style={{ fontSize: '0.5625rem', color: 'var(--text-muted)' }}>(adj)</em></span>
            ))}
          </div>
        )}
      </div>
    )
  }

  const stratTypes = ['rsi_straddle', 'trending_expiry'] as const

  return (
    <div>
      <div className="page-head">
        <h1>My Strategies <InfoTip term="strategyConfig" placement="bottom" /></h1>
        <p className="page-sub">Manage execution sets, start/stop, and monitor all your strategies</p>
      </div>

      {stratTypes.map((stratType) => {
        const meta = strategyMeta[stratType]
        const sets = executions.filter(e => e.strategyType === stratType && e.traderId === traderId)
        const runningSets = sets.filter(e => e.status === 'running')
        const stoppedSets = sets.filter(e => e.status === 'stopped')
        const pausedSets = sets.filter(e => e.status === 'paused')
        const totalPnl = sets.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0)

        return (
          <div key={stratType} className="card" style={{ marginBottom: '1.25rem' }}>
            {/* Strategy Header */}
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span className="card-title" style={{ color: meta.color }}>{meta.icon} {meta.label} {stratType === 'rsi_straddle' ? <InfoTip term="rsiStraddle" /> : <InfoTip term="trending" />}</span>
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                  {sets.length} set{sets.length !== 1 ? 's' : ''} ·{' '}
                  <span className="green">{runningSets.length} live</span> ·{' '}
                  <span>{stoppedSets.length} stopped</span>
                  {pausedSets.length > 0 && <> · <span className="yellow">{pausedSets.length} paused</span></>}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: '0.8125rem', color: totalPnl >= 0 ? 'var(--green)' : 'var(--red)', marginRight: '0.5rem' }}>
                  MTM: {totalPnl >= 0 ? '+' : ''}₹{totalPnl.toLocaleString()}
                </span>
                {stoppedSets.length > 0 && (
                  <button className="btn btn-sm btn-green" onClick={() => handleStartAll(stratType)}>
                    ▶ Start All
                  </button>
                )}
                {runningSets.length > 0 && (
                  <>
                    <button className="btn btn-sm btn-ghost" onClick={() => handleStopAll(stratType)}>
                      ■ Stop All
                    </button>
                    <button className="btn btn-sm btn-red" onClick={() => setPanicModal({ level: 'strategy', target: stratType, label: meta.label })}>
                      ⚠ Panic
                    </button>
                  </>
                )}
                <button className="btn btn-sm btn-primary" onClick={() => setSidebarOpen(stratType)}>
                  + Add Set
                </button>
              </div>
            </div>

            {/* Strategy description */}
            <div style={{
              padding: '0.5rem 1rem',
              background: 'var(--bg-primary)', borderBottom: '1px solid var(--border)',
              fontSize: '0.75rem', color: 'var(--text-secondary)'
            }}>
              {meta.desc}
            </div>

            {/* Execution Cards — same layout as Dashboard */}
            {sets.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                No execution sets for this strategy. Click <strong>+ Add Set</strong> to create one.
              </div>
            ) : (
              <div className="exec-card-list">
                {sets.map((exec) => renderExecCard(exec))}
              </div>
            )}
          </div>
        )
      })}

      {/* ═══ Trader Strategy Sidebar ═══ */}
      {sidebarOpen && (
        <TraderSetSidebar
          initialStratType={sidebarOpen as 'rsi_straddle' | 'trending_expiry'}
          onClose={() => setSidebarOpen(null)}
          onCreate={(data) => {
            createExecution({
              strategyId: data.stratType === 'rsi_straddle' ? 's1' : 's3',
              strategyType: data.stratType,
              setName: data.customName || `${data.stratType === 'rsi_straddle' ? 'RSI' : 'Trend'} — ${data.underlying}`,
              traderId: traderId,
              underlying: data.underlying,
              mode: data.mode,
              brokerId: data.broker,
              status: 'stopped',
              params: data.params as any,
              state: {
                currentPremium: 0, initialPremium: 0, slPrice: 0, targetPrice: 0, reentryCount: 0, pnl: 0,
                mtm: { strategyPnl: 0, setPnl: 0, totalPnl: 0, unrealizedPnl: 0, realizedPnl: 0, lastUpdated: new Date().toISOString() },
                currentStraddle: null,
              },
            })
            addToast(`${data.customName || data.stratType} created on ${data.underlying}`, 'success')
            setSidebarOpen(null)
          }}
        />
      )}

      {/* Partial Exit Modal */}
      {partialModal && (() => {
        const exec = executions.find((e) => e.id === partialModal)
        const p = exec?.params as any
        const totalLots = p?.quantityLots || 1
        const lotSize = getLotSize(exec?.underlying || 'NIFTY')
        const totalQty = totalLots * lotSize

        const generateOptions = (lots: number): { pct: number; lotCount: number }[] => {
          const opts: { pct: number; lotCount: number }[] = []
          opts.push({ pct: 0, lotCount: 0 })
          if (lots <= 10) {
            for (let i = 1; i < lots; i++) {
              opts.push({ pct: Math.round((i / lots) * 100), lotCount: i })
            }
          } else {
            const steps = new Set<number>()
            for (let pct = 10; pct < 100; pct += 10) {
              const lotCount = Math.floor(lots * pct / 100)
              if (lotCount > 0 && lotCount < lots && !steps.has(lotCount)) {
                steps.add(lotCount)
                opts.push({ pct: Math.round((lotCount / lots) * 100), lotCount })
              }
            }
          }
          opts.push({ pct: 100, lotCount: lots })
          return opts
        }

        const ceOptions = generateOptions(totalLots)
        const peOptions = generateOptions(totalLots)
        const ceLots = Math.floor(totalLots * partialCe / 100)
        const peLots = Math.floor(totalLots * partialPe / 100)
        const ceQty = ceLots * lotSize
        const peQty = peLots * lotSize

        return (
          <div className="modal-overlay" onClick={() => setPartialModal(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Partial Exit — {exec?.setName || partialModal}</h3>
                <button className="modal-close" onClick={() => setPartialModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                  Select number of lots to exit for each leg.
                </p>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  Total: {totalLots} Lot{totalLots > 1 ? 's' : ''} × {lotSize} = {totalQty} Qty per leg
                </div>
                <div className="cfg-grid">
                  <div className="cfg-field">
                    <label className="cfg-label">CE Leg — {ceLots} Lot{ceLots !== 1 ? 's' : ''} ({ceQty} Qty)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                      {ceOptions.map((opt) => (
                        <button key={opt.pct} className={`btn btn-sm ${partialCe === opt.pct ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPartialCe(opt.pct)} style={{ minWidth: 44 }}>
                          {opt.lotCount}L
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="cfg-field">
                    <label className="cfg-label">PE Leg — {peLots} Lot{peLots !== 1 ? 's' : ''} ({peQty} Qty)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                      {peOptions.map((opt) => (
                        <button key={opt.pct} className={`btn btn-sm ${partialPe === opt.pct ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPartialPe(opt.pct)} style={{ minWidth: 44 }}>
                          {opt.lotCount}L
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {exec && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-input)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    Exit: CE {ceLots} lot{ceLots !== 1 ? 's' : ''} ({ceQty} qty) + PE {peLots} lot{peLots !== 1 ? 's' : ''} ({peQty} qty)
                    {(ceLots > 0 || peLots > 0) && (
                      <> — Est. P&L: <strong style={{ color: exec.state.pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        ₹{Math.round(exec.state.pnl * ((partialCe + partialPe) / 200)).toLocaleString()}
                      </strong></>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setPartialModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handlePartialExit} disabled={ceLots === 0 && peLots === 0}>
                  Exit {ceLots + peLots} Lot{(ceLots + peLots) !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Panic Confirmation Modal */}
      {panicModal && (
        <div className="modal-overlay" onClick={() => setPanicModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--red)' }}>⚠ Panic Exit <InfoTip term="panicExit" /></h3>
              <button className="modal-close" onClick={() => setPanicModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '1rem' }}>
                Square off <strong>{panicModal.label}</strong>{panicModal.level === 'strategy' ? ' — all running sets' : ''} immediately?
              </p>
              <div style={{ background: 'var(--red-glow)', padding: '0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8125rem' }}>
                This will immediately close all open positions. This cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPanicModal(null)}>Cancel</button>
              <button className="btn btn-red" onClick={handlePanicConfirm}>⚠ Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
